import { Switch, Route, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { SkipToContent, AriaAnnouncerProvider } from "@/components/ui/accessibility";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Chatbot from "@/components/chatbot/Chatbot";

import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import BookingPage from "@/pages/BookingPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import ContactPage from "@/pages/ContactPage";
import AuthPage from "@/pages/AuthPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import NotFound from "@/pages/not-found";

function Router() {
  return (
    <Switch>
      <Route path="/" component={HomePage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/services" component={ServicesPage} />
      <Route path="/booking" component={BookingPage} />
      <Route path="/testimonials" component={TestimonialsPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/blog" component={BlogPage} />
      <Route path="/blog/:slug" component={BlogPostPage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/admin-login" component={AdminLoginPage} />
      
      {/* Protected routes */}
      <ProtectedRoute path="/client-portal" component={ClientPortalPage} />
      <ProtectedRoute path="/admin" component={AdminDashboardPage} adminOnly={true} />
      
      {/* 404 route */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [location] = useLocation();
  const isAdminOrClientPortal = location.startsWith("/admin") || location.startsWith("/client-portal");
  const isAuthPage = location === "/auth" || location === "/admin-login";

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          {/* Accessibility components */}
          <SkipToContent />
          <AriaAnnouncerProvider />
          
          {!isAdminOrClientPortal && !isAuthPage && <Header />}
          <main id="main-content" className={`min-h-screen ${isAuthPage ? 'p-0' : 'py-6'}`}>
            <Router />
          </main>
          {!isAdminOrClientPortal && !isAuthPage && <Footer />}
          {!isAdminOrClientPortal && !isAuthPage && <Chatbot />}
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
